import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'react-bootstrap';
import { SIZE } from 'baseui/button';
import store from 'store';
import { paths } from 'frameworks_drivers/routes';
import { TrackerUtil } from 'tracking/TrackerUtil';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardSteps, WizardStepsContainer } from 'presentation/components/WizardSteps';
import { Button } from '@techem/ui-components';
import IconRadioGroup from 'presentation/components/IconRadioGroup';
import option1Icon from 'presentation/assets/icons/png/3x/icn-heizung-01@3x.png';
import option2Icon from 'presentation/assets/icons/png/3x/icn-heizung-02@3x.png';
import option3Icon from 'presentation/assets/icons/png/3x/icn-heizung-03@3x.png';
import { setHeatingType as setHeatingTypeInStore } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import { RadioOptionKeys, translationKeyForRadioOption } from 'tools/RadioOptionMapper';
import { useNavigate } from 'react-router-dom';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import useHotKey from 'tools/hooks/useHotKey';

const Component: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const prevPath = paths.HotWaterDelivery;

  useEffect(() => {
    if (
      !DISABLE_FLOW_DEEP_LINKING_PROTECTION &&
      // Assumption: previous page has to be filled with user input before the user is allowed to visit this page
      store.getState().onboardingInfo.centralHotWaterDelivery === ''
    ) {
      navigate(prevPath, { replace: true });
    }
  }, [prevPath, navigate]);

  const [heatingType, setHeatingType] = useState<string>(
    store.getState().onboardingInfo.heatingType
  );

  const radioData = [
    {
      icon: option1Icon,
      key: RadioOptionKeys.HeatingType.RadiatorOnly,
      trackingId: 'radio-button_radiator'
    },
    {
      icon: option2Icon,
      key: RadioOptionKeys.HeatingType.UnderFloorHeatingOnly,
      trackingId: 'radio-button_underfloor-heating'
    },
    {
      icon: option3Icon,
      key: RadioOptionKeys.HeatingType.RadiatorAndUnderfloorHeating,
      trackingId: 'radio-button_mix'
    }
  ].map((opt) => ({
    icon: opt.icon,
    label: t(translationKeyForRadioOption(opt.key)),
    optionKey: opt.key,
    trackingId: opt.trackingId
  }));

  useHotKey((event) => {
    if (event.key === '1') setHeatingType(radioData[0].optionKey);
    if (event.key === '2') setHeatingType(radioData[1].optionKey);
    if (event.key === '3') setHeatingType(radioData[2].optionKey);
  });

  const handleSubmit = () => {
    store.dispatch(setHeatingTypeInStore(heatingType));

    TrackerUtil.trackNextBtnClick();
    navigate(paths.HeatingSources);
  };

  const canBeSubmitted = () => {
    return heatingType !== '';
  };

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer>
        <WizardSteps currentStep={5} totalSteps={6} />
      </WizardStepsContainer>
      <TitleWithPrevPageLink onPrevClick={handlePrevBtnClicked} title={t('pageHeatingTypeTitle')} />

      <form>
        <Row className="justify-content-center mt-3">
          <Col lg={10} md={10}>
            <fieldset>
              <legend id="heating-type-label" className="visually-hidden">
                {t('pageHeatingTypeTitle')}
              </legend>
              <IconRadioGroup
                radioData={radioData}
                handleChange={(newVal: string) => setHeatingType(newVal)}
                currentSelection={() => heatingType}
                aria-labelledby="heating-type-label"
              />
            </fieldset>
          </Col>
        </Row>

        <Row className="justify-content-center py-5">
          <Col className="col-auto">
            <Button
              onClick={handleSubmit}
              disabled={!canBeSubmitted()}
              size={SIZE.large}
              type="submit"
              additionalTestId="next"
            >
              {t('nextBtnText')}
            </Button>
          </Col>
        </Row>
      </form>
    </Container>
  );
};

export default Component;
